/**
 * # dxfBridge.import
 *
 * Top-level DXF → DrawingModel orchestrators.
 *
 * This module drives the read path of the bridge:
 *
 *   importDxfStringToDrawingDocument(text, opts?)
 *     -> parseDxfDocument                       [@dxf-ai-editor/dxf-io]
 *     -> importDxfDocumentToDrawingDocument(dxfDoc, opts?)
 *         -> importStyleTables(dxfDoc)
 *             -> importLayers, importMultileaderStyles
 *             -> dxfTextStyleToDrawingTextStyle / dxfDimStyleToDrawingDimStyle /
 *                dxfMLeaderStyleToDrawingMLeaderStyle
 *                [./dxfBridge.drawingToDxf]
 *         -> importBlockDefinitions(dxfDoc)
 *             -> dxfBlockDefinitionToDrawingBlock
 *                 -> extractAttdefFromGroups     [./dxfBridge.drawingToDxf]
 *                 -> toDrawingEntity             [./dxfBridge.drawingToDxf]
 *         -> buildImportedSpaces(dxfDoc, toDrawingEntity, opts)
 *             [./dxfBridge.spaces]
 *         -> attachDynamicBlockMetadata / detectDynamicBlocks /
 *            extractDynamicBlockParams / attachInsertCustomProperties
 *             [./dxfBridge.dynamic]
 *         -> collectUnsupportedEntityGroups (raw ENTITIES passthrough)
 *
 *   importDxfStringToDrawingDocumentWithProgress(text, opts?)
 *     -> emits progress callbacks (tokenize / sections / entities / bridge)
 *       between yields to let the React UI paint, then delegates to the
 *       synchronous importer.
 *
 * Split from the monolithic `dxfBridge.ts` to respect the CLAUDE.md
 * 2000-line guardrail. All public entry points remain re-exported from
 * `dxfBridge.ts` so consumers don't need to update their imports.
 */

import {
  ASSOC_ARRAY_PLACEHOLDER_CODE,
  ASSOC_ARRAY_PLACEHOLDER_INDEX_PREFIX,
  getBlocksSection,
  getEntitiesSection,
  getObjectsSection,
  getSupportedEntities,
  getTablesSection,
  parseDxfDocumentStreaming,
  readAnnotativeVarsFromHeader,
  readDocumentVarsFromHeader,
  readDynVarsFromHeader,
  readLinetypeVarsFromHeader,
  readPointVarsFromHeader,
  readSnapVarsFromHeader,
  resolveAnnotativeContexts,
  resolveAssocArrays,
  serializeAssocArrayObject,
  serializeSpatialFilter,
  SPATIAL_FILTER_PLACEHOLDER_CODE,
  SPATIAL_FILTER_PLACEHOLDER_INDEX_PREFIX,
  type DxfArrayRectEntity,
  type DxfAssocArrayObject,
  type DxfBlockDefinition,
  type DxfBlocksSection,
  type DxfDocument,
  type DxfEntity,
  type DxfGroup,
  type DxfLayerEntry,
  type DxfSupportedEntity,
  type DynVarHeaderKey,
  isTopLevelGroup,
  trackNestedBlockDepth,
} from "@dxf-ai-editor/dxf-io";

import type { ImportProgressCallback } from "./importPhases";

import {
  buildDefaultStandardDimensionStyle,
  buildDefaultStandardMultileaderStyle,
  buildDefaultStandardTextStyle,
  dxfDimStyleToDrawingDimStyle,
  dxfMLeaderStyleToDrawingMLeaderStyle,
  dxfTextStyleToDrawingTextStyle,
  extractAttdefFromGroups,
  toDrawingEntity,
} from "./dxfBridge.drawingToDxf";

import { applyAssocArrayResolution } from "./dxfBridge.arrayAssocImport";
import { importBlockRecords } from "./dxfBridge.blockRecords";
import { importLayouts } from "./dxfBridge.layouts";
import { rawPassthroughGroupForEntity } from "./dxfBridge.seqendPassthrough";
import { importLinetypes } from "./dxfBridge.linetypes";
import {
  buildImportedSpaces,
  DEFAULT_PAPER_FALLBACK_LAYOUT_NAME,
} from "./dxfBridge.spaces";
import {
  buildHealResolution,
  type HealResolution,
} from "./referenceHeal.entity";
import {
  attachDynamicBlockMetadata,
  attachInsertCustomProperties,
  detectDynamicBlocks,
  extractDynamicBlockParams,
} from "./dxfBridge.dynamic";

import {
  clampFillmode,
  clampMirrtext,
  layerDefinitionSchema,
  type AnnotScaleContext,
  type AssocArrayCarrierObject,
  type BlockChildEntity,
  type BlockDefinition,
  type DrawingDocument,
  type DrawingEntity,
  type LayerDefinition,
  type MultileaderStyle,
  type RawEntityGroup,
  type ViewportEntity,
  type SavedView,
  type StyleTables,
} from "./model";

import { cecolorFromHeader } from "./color";
import { applyViewportClipAndMembership } from "./viewportClipFinalize";
import { resolveImportedVpFrozen } from "./viewportFrozenLayersImport";

export type ImportDxfDocumentToDrawingDocumentOptions = {
  documentId?: string;
  title?: string;
  fallbackLayoutName?: string;
  /**
   * Optional progress callback invoked at phase boundaries and, for the
   * high-cardinality entities/merge phases, approximately once per 100
   * items. Receives `(phase, percent 0-100, itemsProcessed, itemsTotal)`.
   *
   * The importer emits progress for the phases owned by the bridge
   * (tokenize, sections, entities, bridge). The caller (editorStore)
   * wraps it with fetch/decode/merge/sync markers around the bridge call
   * so the full AutoCAD-style "Processing Objects" dialog can walk all
   * eight phases declared in `importPhases.ts`.
   */
  onProgress?: ImportProgressCallback;
  /**
   * Phase 4 Wave 2 Task 3 — streaming hook for the model-/paper-space
   * routing pass. Forwarded as-is to {@link buildImportedSpaces}
   * (`BuildImportedSpacesOptions.onEntityImported`). Default behaviour
   * (option omitted): entities land in `space.entities[]` as a plain
   * array. Opt-in callers (editorStore.importDxfFileAsync, etc.) use
   * this hook to stream entities into the compact cache while the
   * parser appends, so the peak intermediate footprint on 500k+ entity
   * drawings can be removed in a future iteration without re-walking
   * the array.
   *
   * Currently the importer keeps the in-memory `space.entities[]`
   * populated regardless — switching to a streaming-only (empty
   * `space.entities[]`) emission requires also updating downstream
   * consumers (`rebuildEntityMetadataIndexFromImport`,
   * `computeCoordinateOriginForImport`, etc.) that walk the array.
   * This is a follow-up; for Wave 2 we ship the hook + flag, default-off.
   *
   * PR1b Codex P2 — forwarded as-is to {@link buildImportedSpaces}. A healing
   * hook (streaming worker) returns the HEALED entity so the routing layer
   * writes it back into the retained shell Viewport; a legacy hook returns
   * `void` (unchanged behaviour).
   */
  onEntityImported?: (
    entity: DrawingEntity,
    spaceId: string,
  ) => DrawingEntity | void;
  /**
   * Phase 4 Wave 2 Task 4 — streaming-only mode forwarded to
   * {@link buildImportedSpaces}.
   *
   * Effective only when `onEntityImported` is also set. When `true`,
   * the routing layer streams every converted entity through the hook
   * but does NOT push them into `space.entities[]`. The returned
   * `DrawingDocument`'s spaces carry empty `entities` arrays — the
   * caller is responsible for sourcing them from wherever the hook
   * sent them (typically the editorStore compact cache).
   *
   * **Correctness trade-off:** the post-routing enrichment passes
   * (`attachInsertCustomProperties` for dynamic-block parameter
   * defaults, `resolveAnnotativeContexts` for per-entity
   * `annotativeContexts`) walk `space.entities` after the bridge
   * builds the spaces. With `discardEntitiesAfterStreaming: true`
   * those passes see empty arrays and become no-ops on streamed
   * entities. Streaming mode therefore ships entities WITHOUT
   * customProperties defaults and WITHOUT annotativeContexts attached.
   *
   * This is a documented limitation accepted as the cost of unblocking
   * 685k+ entity drawings (P35Р). A follow-up may move enrichment
   * inline with the hook (or push it into the consumer pipeline) to
   * close this gap.
   *
   * Default: `false`.
   */
  discardEntitiesAfterStreaming?: boolean;
  /**
   * Codex P5 / Option B follow-up (2026-05-18) — drain top-level
   * `document.sections[entities].entities` as the bridge consumes
   * each entity. See `BuildImportedSpacesOptions.drainTopLevelEntities`
   * for the full rationale. Forwarded verbatim to `buildImportedSpaces`.
   *
   * Only safe in single-pass consumers (worker) where the document is
   * not re-read after bridge completes. Default: `false`.
   */
  drainTopLevelEntities?: boolean;
};

/**
 * Yield to the event loop between phases so React can paint the
 * progress dialog update before the next phase starts. `queueMicrotask`
 * alone does NOT let the browser paint — we need a macrotask. A short
 * `setTimeout(resolve, 0)` does the job; the 0-delay macrotask is
 * drained between render commits.
 */
function yieldForPaint(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof setTimeout === "function") {
      setTimeout(resolve, 0);
    } else {
      queueMicrotask(resolve);
    }
  });
}

/* ── Block Definitions: DXF → DrawingModel ──────────── */

export function importLayers(dxfLayers: DxfLayerEntry[]): LayerDefinition[] {
  if (dxfLayers.length === 0) {
    return [layerDefinitionSchema.parse({ name: "0" })];
  }
  const layers = dxfLayers.map((dxfLayer): LayerDefinition => {
    // Negative color number means the layer is off (not visible)
    const colorValue = dxfLayer.color;
    const visible = colorValue >= 0;
    const absColor = Math.abs(colorValue);
    // Flag bits: bit 0 (1) = frozen, bit 2 (4) = locked
    const frozen = (dxfLayer.flags & 1) !== 0;
    const locked = (dxfLayer.flags & 4) !== 0;
    return layerDefinitionSchema.parse({
      name: dxfLayer.name,
      color: absColor,
      visible,
      frozen,
      locked,
      linetype: dxfLayer.linetype,
      lineweight: dxfLayer.lineweight,
      plottable: dxfLayer.plottable,
      description: dxfLayer.description ?? "",
      flags: dxfLayer.flags,
      handle: dxfLayer.handle ?? "",
      // T9b — carry the record's `330` owner + `390` plot-style + `347`
      // material pointers verbatim (AutoCAD always writes them on R2000+ /
      // R2007+ layers) so a faithful re-export keeps the real values.
      ...(dxfLayer.ownerHandle !== undefined
        ? { ownerHandle: dxfLayer.ownerHandle }
        : {}),
      ...(dxfLayer.plotStyleHandle !== undefined
        ? { plotStyleHandle: dxfLayer.plotStyleHandle }
        : {}),
      ...(dxfLayer.materialHandle !== undefined
        ? { materialHandle: dxfLayer.materialHandle }
        : {}),
    });
  });
  // Ensure layer "0" exists (AutoCAD always has it)
  if (!layers.find((l) => l.name === "0")) {
    layers.unshift(layerDefinitionSchema.parse({ name: "0" }));
  }
  return layers;
}

export function importStyleTables(document: DxfDocument): StyleTables {
  const tablesSection = getTablesSection(document);
  const objectsSection = getObjectsSection(document);

  if (!tablesSection) {
    return {
      textStyles: [buildDefaultStandardTextStyle()],
      dimensionStyles: [buildDefaultStandardDimensionStyle()],
      multileaderStyles: importMultileaderStyles(objectsSection),
      layers: [layerDefinitionSchema.parse({ name: "0" })],
    };
  }

  const textStyles =
    tablesSection.textStyles.length > 0
      ? tablesSection.textStyles.map(dxfTextStyleToDrawingTextStyle)
      : [buildDefaultStandardTextStyle()];

  const dimensionStyles =
    tablesSection.dimensionStyles.length > 0
      ? tablesSection.dimensionStyles.map(dxfDimStyleToDrawingDimStyle)
      : [buildDefaultStandardDimensionStyle()];

  // PR1b Codex P2 — AutoCAD ALWAYS has a "Standard" STYLE and DIMSTYLE. A
  // legal-but-dirty DXF can ship a NON-EMPTY table that OMITS "Standard"; the
  // empty-table branches above only seed it for the EMPTY case. Mirror the
  // multileader path (`importMultileaderStyles` always guarantees "Standard")
  // so the import baseline — and the STREAMING shell document built from these
  // tables — always carries "Standard", matching the whole-doc heal that
  // re-points dangling text/dim style refs to it (referenceHeal P2 fixup).
  // PR1b Codex P2 (iter-3) — style-table names resolve CASE-INSENSITIVELY (the
  // audit/resolution `nameIndex` lowercases every key). A table that already
  // ships "STANDARD" / "standard" MUST NOT get a SECOND "Standard" record —
  // that produces a DUPLICATE_TABLE_ENTRY_NAME + two case-equivalent names on
  // export. Seed only when NO case-insensitive match exists.
  if (!textStyles.some((s) => s.name.toLowerCase() === "standard")) {
    textStyles.unshift(buildDefaultStandardTextStyle());
  }
  if (!dimensionStyles.some((s) => s.name.toLowerCase() === "standard")) {
    dimensionStyles.unshift(buildDefaultStandardDimensionStyle());
  }

  return {
    textStyles,
    dimensionStyles,
    multileaderStyles: importMultileaderStyles(objectsSection),
    layers: importLayers(tablesSection.layers),
  };
}

/**
 * PR1b Phase 2b — build a per-entity heal `Resolution` from a raw `DxfDocument`
 * BEFORE the (entity-streaming) bridge runs. The streaming worker discards
 * `space.entities[]`, so the whole-document heal cannot see streamed entities;
 * instead it heals each chunk's entities in the `onEntityImported` hook against
 * this resolution (plan §12 option (a)).
 *
 * The resolution only needs the SYMBOL TABLES (layers / text-, dim-,
 * mleader-styles / blocks / linetypes / annotation scales) — these are parsed
 * from the `DxfDocument` independently of the entity-routing pass, so the
 * resolution is available BEFORE the first entity chunk is converted. We reuse
 * the SAME importers the full bridge uses (`importStyleTables` /
 * `importBlockDefinitions` / `importLinetypes` + the same acadScales read) so
 * there is no parsing-logic drift — the resolution sees exactly the names the
 * assembled document would carry. The shell carries empty `spaces` (resolution
 * never reads entities). PURE / never throws (mirrors the importers + audit).
 *
 * Cost: O(symbol-table entries) — kilobytes, NOT the per-entity hot path. Built
 * ONCE per import; healing each entity against it is O(1) per entity.
 */
export function buildHealResolutionFromDxf(
  document: DxfDocument,
): HealResolution {
  const objectsSection = getObjectsSection(document);
  // Annotation-scale catalogue — same first-wins, skip-hidden read as the full
  // bridge (lines below in importDxfDocumentToDrawingDocument). buildResolution
  // always seeds the built-in "1:1", so an absent catalogue still resolves it.
  const annotationScales: { name: string; scaleFactor: number }[] = [];
  if (objectsSection?.acadScales) {
    for (const s of objectsSection.acadScales) {
      if (s.isHidden) continue;
      if (annotationScales.some((x) => x.name === s.name)) continue;
      annotationScales.push({ name: s.name, scaleFactor: s.scaleFactor });
    }
  }
  const tablesSection = getTablesSection(document);
  const shell = {
    spaces: [],
    styleTables: importStyleTables(document),
    blocks: importBlockDefinitions(getBlocksSection(document)),
    linetypes: importLinetypes(
      tablesSection?.linetypes ?? [],
      tablesSection?.textStyles ?? [],
    ),
    ...(annotationScales.length > 0 ? { annotationScales } : {}),
  } as unknown as DrawingDocument;
  return buildHealResolution(shell);
}

export function importMultileaderStyles(
  objectsSection: ReturnType<typeof getObjectsSection>,
): MultileaderStyle[] {
  if (!objectsSection || objectsSection.multileaderStyles.length === 0) {
    return [buildDefaultStandardMultileaderStyle()];
  }

  const styles = objectsSection.multileaderStyles.map(
    dxfMLeaderStyleToDrawingMLeaderStyle,
  );

  // AutoCAD always provides a "Standard" MLEADERSTYLE — guarantee one
  // exists even if the source DXF omitted it.
  if (!styles.find((s) => s.name === "Standard")) {
    styles.unshift(buildDefaultStandardMultileaderStyle());
  }

  return styles;
}

// Convert a single DxfBlockDefinition (from the parser) into the
// drawing-model BlockDefinition used by the renderer. Child entities
// go through the same toDrawingEntity pipeline as model-space entities
// so every primitive (LINE, CIRCLE, ARC, TEXT, MTEXT, LWPOLYLINE,
// INSERT, …) renders with full fidelity. ATTDEF records are extracted
// from the unsupported-entity branch because the full ATTDEF primitive
// is not yet in the supported set.
/**
 * T9b — extract the ENDBLK's OWN group-5 handle from the parser's verbatim
 * `endblkGroups` (`0 ENDBLK` marker first, then its groups). Only the first
 * TOP-LEVEL `5` counts — a `5` inside a `102 {…}` reactor / xdictionary block
 * is nested data, never the record handle. `undefined` when the source ENDBLK
 * carried no handle (R12) or the block had no ENDBLK groups at all.
 */
function readEndblkHandle(endblkGroups: DxfGroup[]): string | undefined {
  let depth = 0;
  for (const g of endblkGroups) {
    depth = trackNestedBlockDepth(g, depth);
    if (!isTopLevelGroup(g, depth)) continue;
    if (g.code === 5) {
      const handle = g.value.trim();
      return handle.length > 0 ? handle : undefined;
    }
  }
  return undefined;
}

export function dxfBlockDefinitionToDrawingBlock(
  block: DxfBlockDefinition,
): BlockDefinition {
  const blockSpaceId = `__block:${block.name}`;
  const entities: BlockChildEntity[] = [];
  const rawEntityGroups: RawEntityGroup[] = [];
  let attdefIndex = 0;
  let childIndex = 0;
  let prevBlockEntity: DxfEntity | undefined;
  for (const rawEntity of block.entities) {
    const prev = prevBlockEntity;
    prevBlockEntity = rawEntity;
    if (rawEntity.kind === "supported-entity") {
      // Skip ATTRIB / SEQEND entities here — AutoCAD does not ordinarily
      // place ATTRIBs inside a BLOCK definition (they follow INSERTs
      // instead). If they slip through, parseBlocksSection has already
      // attached them to the owning INSERT so we just skip any that
      // remain as siblings. EXCEPTION (SL-22 F1): a SEQEND that terminates a
      // heavy POLYLINE (prev is an unsupported POLYLINE/VERTEX) is captured as
      // a raw passthrough so the terminator is not dropped from the block.
      if (rawEntity.type === "ATTRIB" || rawEntity.type === "SEQEND") {
        const seqendRaw = rawPassthroughGroupForEntity(prev, rawEntity);
        if (seqendRaw) rawEntityGroups.push(seqendRaw);
        continue;
      }
      const converted = toDrawingEntity(
        rawEntity,
        rawEntity.common.handle?.trim() ||
          `block-${block.name}-${childIndex++}`,
        blockSpaceId,
      );
      // Only primitives that are valid BlockChildEntity pass through.
      //
      // Batch-7 (S-BLOCKCHILD-{HATCH,DIM,MLEADER,ALLOWLIST}): Ellipse,
      // Spline, Hatch, Dimension and Multileader are now part of
      // blockChildEntitySchema (model.ts) and ARE legal block-definition
      // children per the Autodesk DXF reference + ezdxf BlockLayout, so we
      // keep the parsed-and-typed child here for import→export preservation.
      // These types come in as `supported-entity` (toDrawingEntity yields a
      // typed result) and are NOT captured in rawEntityGroups, so keeping
      // the typed child does NOT double-emit on export (unlike Attdef, which
      // round-trips via rawEntityGroups — see the de-dup at export.ts).
      //
      // EXCLUDED — Point: a block-child Point is a CATEGORY-C render concern
      // (renders at origin = visible regression) owned by a DEFUSE render fix.
      // ARRAYRECT / VIEWPORT / WIPEOUT stay dropped. Revision Cloud IS now
      // included (@codex #422 P2-A): a block-child `0 LWPOLYLINE` + RevcloudProps
      // reclassifies to REVCLOUD → "Revision Cloud"; full rationale in model.ts.
      if (
        converted.type === "Line" ||
        converted.type === "Circle" ||
        converted.type === "Arc" ||
        converted.type === "Ellipse" ||
        converted.type === "Spline" ||
        converted.type === "LWPOLYLINE" ||
        converted.type === "Text" ||
        converted.type === "MText" ||
        converted.type === "Block" ||
        converted.type === "Hatch" ||
        converted.type === "Dimension" ||
        converted.type === "Multileader" ||
        converted.type === "Revision Cloud" ||
        // Legacy heavy POLYLINE, ACAD_TABLE and OLE2FRAME are now parsed and
        // typed (`blockChildEntitySchema` carries all three — see model.ts for
        // why they are legal block children and why this repo's own
        // `abi1-test.dxf` fixture depends on it: eleven of its heavy POLYLINEs
        // live INSIDE block definitions). They arrive as `supported-entity`
        // and are NOT captured in rawEntityGroups, so keeping the typed child
        // does not double-emit on export.
        converted.type === "Polyline" ||
        converted.type === "Table" ||
        converted.type === "OLE Frame" ||
        converted.type === "Attdef"
      ) {
        entities.push(converted);
      }
    } else if (rawEntity.kind === "unsupported-entity") {
      if (rawEntity.type === "ATTDEF") {
        // ATTDEF is still kept in `block.entities` as a typed AttdefEntity
        // so attribute-sync commands (ATTSYNC, BATTMAN, ATTSYNC) can work
        // with structured fields. Raw groups are captured in parallel via
        // rawEntityGroups below so the writer re-emits them verbatim for
        // byte-level round-trip fidelity (E2). The writer is expected to
        // skip any typed Attdef children whose raw counterpart exists in
        // rawEntityGroups to avoid double-emission.
        const attdef = extractAttdefFromGroups(
          rawEntity.groups,
          attdefIndex++,
          blockSpaceId,
        );
        if (attdef) entities.push(attdef);
        // fall through — also capture raw groups
      }
      // E2 fidelity: capture unsupported block children verbatim so the writer
      // re-emits them into the BLOCKS section. `rawEntity.groups` includes the
      // `{0, TYPE}` header — skip it here (the schema stores `entityType`
      // separately and the writer re-prepends it on emission).
      const groupsAfterHeader = rawEntity.groups.slice(1).map((g) => ({
        code: g.code,
        value: g.value,
      }));
      rawEntityGroups.push({
        entityType: rawEntity.type,
        groups: groupsAfterHeader,
      });
    }
  }
  // Phase FIN.3 — derive xrefStatus from the BLOCK header. AutoCAD sets
  // bit 4 (xref) or bit 8 (xref-overlay) on `flags` when the block is an
  // external reference. On parse we tag it "resolved" by default; the
  // bridge cannot read external files so a follow-up XRELOAD with a
  // valid path turns it into "resolved" with cached content. If the
  // path is empty we surface "not-found" so the renderer paints the
  // red dashed indicator immediately.
  const isXref = (block.flags & 0x04) !== 0 || (block.flags & 0x08) !== 0;
  const xrefStatus = isXref
    ? block.xrefPathName.trim().length === 0
      ? ("not-found" as const)
      : ("resolved" as const)
    : ("resolved" as const);
  // T9b — the ENDBLK's own group-5 (the first top-level `5` after `0 ENDBLK`,
  // outside any `102 {…}` block) so a re-export reconstructs an ENDBLK with
  // the SAME handle instead of a handle-less one.
  const endblkHandle = readEndblkHandle(block.endblkGroups);
  return {
    name: block.name,
    handle: block.handle,
    ownerHandle: block.ownerHandle,
    ...(endblkHandle !== undefined ? { endblkHandle } : {}),
    layer: block.layer,
    flags: block.flags,
    basePoint: {
      x: block.basePoint.x,
      y: block.basePoint.y,
      z: block.basePoint.z,
    },
    xrefPathName: block.xrefPathName,
    description: block.description,
    entities,
    // Default to static — detectDynamicBlocks (called after bulk
    // construction) overwrites the flag when the OBJECTS-section markers
    // say otherwise. This keeps the direct constructor path type-safe.
    dynamicBlock: false,
    xrefStatus,
    // Omit `rawEntityGroups` when empty to keep the in-memory shape
    // aligned with the optional schema and avoid noise in snapshot tests.
    ...(rawEntityGroups.length > 0 ? { rawEntityGroups } : {}),
  };
}

export function importBlockDefinitions(
  blocksSection: DxfBlocksSection | null,
): Record<string, BlockDefinition> {
  if (!blocksSection) return {};
  const out: Record<string, BlockDefinition> = {};
  for (const block of blocksSection.blocks) {
    if (!block.name) continue;
    out[block.name] = dxfBlockDefinitionToDrawingBlock(block);
  }
  return out;
}

/**
 * Build the bridge model's verbatim `rawObjectGroups` copy from the parsed
 * OBJECTS section, SUBSTITUTING each SPATIAL_FILTER and AcDbAssocArray
 * placeholder sentinel with the re-serialized record's groups (SPATIAL_FILTER:
 * Codex #319 P2, finding 6 — P1 data-loss; AcDbAssocArray: docs/MASTERPLAN.md
 * N9, AW-0-OBJECTS-BUCKET-ORDER — the byte-position residual AW-0 explicitly
 * deferred).
 *
 * The dxf-io parser pulls each such record OUT of `rawObjectGroups` into a
 * typed bucket (`section.spatialFilters[]` / `section.assocArrays[]`) and
 * leaves a single `{ code: <PLACEHOLDER_CODE>, value: <handle | #idx:N> }`
 * sentinel at the record's position. The bridge model does NOT carry either
 * typed bucket, and `dxfBridge.export` builds the OBJECTS section ONLY from
 * `rawObjectGroups`. For SPATIAL_FILTER the OLD code merely FILTERED the
 * sentinel out (real data loss — see the P1 above); for AcDbAssocArray the
 * OLD code (N9's fix target) resolved the graph correctly but RE-APPENDED it
 * after the whole `rawObjectGroups` catch-all instead of substituting it in
 * position — semantically lossless but byte-position-wrong for any array
 * interleaved among unrelated OBJECTS records (MATERIAL / XRECORD / GROUP /
 * …).
 *
 * Substituting the sentinel with the verbatim re-serialized record (resolved by
 * the same stable handle / `#idx:` positional key the dxf-io writer uses, see
 * `serializeObjectsSection`) makes the model's `rawObjectGroups` carry the full
 * record, so the export's rawObjectGroups-only path re-emits it IN POSITION.
 * No deps:graph cycle: `drawing-model` already imports the dxf-io read path
 * (`parseDxfDocument`, the two `*_PLACEHOLDER_CODE` constants); the
 * substitution reuses dxf-io's `serializeSpatialFilter` /
 * `serializeAssocArrayObject` along the SAME existing `drawing-model → dxf-io`
 * edge (no new reverse dependency).
 */
function buildBridgeRawObjectGroups(
  objectsSection: NonNullable<ReturnType<typeof getObjectsSection>>,
): { code: number; value: string }[] {
  const spatialFilters = objectsSection.spatialFilters ?? [];
  const byHandle = new Map<string, (typeof spatialFilters)[number]>();
  for (const f of spatialFilters) {
    if (f.handle !== "") byHandle.set(f.handle, f);
  }
  // N9 — same handle-keyed resolution map for AcDbAssocArray graph records.
  const assocArrays = objectsSection.assocArrays ?? [];
  const assocByHandle = new Map<string, DxfAssocArrayObject>();
  for (const a of assocArrays) {
    if (a.handle !== "") assocByHandle.set(a.handle, a);
  }
  // @codex local review (N9 round-2) — a caller that constructs a DxfDocument
  // DIRECTLY (bypassing parseDxfDocument / parseDxfDocumentStreaming — e.g. a
  // hand-built synthetic OBJECTS section, or any future producer that
  // populates `spatialFilters` / `assocArrays` without going through the real
  // parser) carries a typed record with NO matching rawObjectGroups sentinel.
  // Every REAL production import path (dxf-parser.worker.ts →
  // parseDxfDocumentStreaming, parseDxfDocument) routes through the shared
  // `parseObjectsSection`, which ALWAYS pairs a divert with a sentinel — so
  // this cannot happen there — but CLAUDE.md Priority #1 (no dropped groups)
  // does not get to assume every future/test caller honours that invariant.
  // Track which typed records a sentinel actually claims, and flush anything
  // left over AFTER the main loop — mirrors the dxf-io writer's identical
  // "Codex #319 P2, finding 7" sentinel-less flush exactly, now at the bridge
  // layer for BOTH typed buckets (SPATIAL_FILTER not previously guarded here
  // either — this closes that pre-existing, not N9-introduced, gap too).
  const emittedFilters = new Set<(typeof spatialFilters)[number]>();
  const emittedAssocArrays = new Set<DxfAssocArrayObject>();
  const out: { code: number; value: string }[] = [];
  for (const g of objectsSection.rawObjectGroups) {
    if (g.code === SPATIAL_FILTER_PLACEHOLDER_CODE) {
      // Resolve the record by its stable handle, or by the prefixed positional
      // index for a handle-less (malformed/synthetic) record — mirrors the
      // dxf-io writer's sentinel substitution exactly.
      const filter = g.value.startsWith(SPATIAL_FILTER_PLACEHOLDER_INDEX_PREFIX)
        ? spatialFilters[
            Number(g.value.slice(SPATIAL_FILTER_PLACEHOLDER_INDEX_PREFIX.length))
          ]
        : byHandle.get(g.value);
      // Defensive: if the record cannot be resolved, DROP the sentinel (never
      // leak a -1 group into the model — it is not a real DXF group).
      if (filter) {
        emittedFilters.add(filter);
        for (const sg of serializeSpatialFilter(filter)) {
          out.push({ code: sg.code, value: sg.value });
        }
      }
      continue;
    }
    if (g.code === ASSOC_ARRAY_PLACEHOLDER_CODE) {
      // N9 — substitute the AcDbAssocArray graph record at this position.
      // Same resolution + defensive-skip contract as the SPATIAL_FILTER
      // branch above (mirrors dxf-io's writer.ts identically — see that
      // branch's full @codex PR #551 round 5/6 history for why an
      // unresolvable match is DROPPED, not passed through).
      const assoc = g.value.startsWith(ASSOC_ARRAY_PLACEHOLDER_INDEX_PREFIX)
        ? assocArrays[
            Number(g.value.slice(ASSOC_ARRAY_PLACEHOLDER_INDEX_PREFIX.length))
          ]
        : assocByHandle.get(g.value);
      if (assoc) {
        emittedAssocArrays.add(assoc);
        for (const sg of serializeAssocArrayObject(assoc)) {
          out.push({ code: sg.code, value: sg.value });
        }
      }
      continue;
    }
    out.push({ code: g.code, value: g.value });
  }
  // Flush any typed record that no sentinel claimed (mirrors the writer's
  // identical fallback) — a synthetic/test-built section carrying the typed
  // list without a position marker must not silently lose its content.
  for (const f of spatialFilters) {
    if (emittedFilters.has(f)) continue;
    for (const sg of serializeSpatialFilter(f)) {
      out.push({ code: sg.code, value: sg.value });
    }
  }
  for (const a of assocArrays) {
    if (emittedAssocArrays.has(a)) continue;
    for (const sg of serializeAssocArrayObject(a)) {
      out.push({ code: sg.code, value: sg.value });
    }
  }
  return out;
}

export function importDxfDocumentToDrawingDocument(
  document: DxfDocument,
  options: ImportDxfDocumentToDrawingDocumentOptions = {},
): DrawingDocument {
  const {
    documentId = "dxf-import",
    title = "Imported DXF",
    fallbackLayoutName = DEFAULT_PAPER_FALLBACK_LAYOUT_NAME,
  } = options;

  // E2 fidelity — capture top-level unsupported ENTITIES siblings (SPLINE,
  // ACAD_TABLE, … + heavy-POLYLINE terminating SEQENDs, SL-22 F1) verbatim.
  // MUST precede buildImportedSpaces: the streaming path (drainTopLevelEntities)
  // DRAINS + CLEARS section.entities, so a post-drain read drops every sibling
  // incl. the SEQEND (@codex SL-22 round-2 P2). One call — no double-collect.
  const rawEntityGroups = collectUnsupportedEntityGroups(document);

  // Multi-layout routing moved to dxfBridge.spaces.ts — see that module header
  // for the full rationale (toDrawingEntity callback avoids a circular module
  // dependency). `xdataAuthoritativeVpHandles` is captured there PRE-DRAIN for
  // EXPORT-VP-FROZEN (@codex #486 P2).
  const { spaces, layoutsBySpaceId, xdataAuthoritativeVpHandles } =
    buildImportedSpaces(document, toDrawingEntity, {
      fallbackLayoutName,
      // Phase 4 Wave 2 Task 3 — forward the streaming hook (undefined ≡
      // no-op, so backward-compatible with all existing callers).
      ...(options.onEntityImported !== undefined
        ? { onEntityImported: options.onEntityImported }
        : {}),
      // Phase 4 Wave 2 Task 4 — forward the streaming-only flag. Only
      // takes effect when `onEntityImported` is also set (the routing
      // layer guards against silent entity loss).
      ...(options.discardEntitiesAfterStreaming === true
        ? { discardEntitiesAfterStreaming: true }
        : {}),
      ...(options.drainTopLevelEntities === true
        ? { drainTopLevelEntities: true }
        : {}),
    });

  const finalSpaces =
    spaces.length > 0
      ? spaces
      : [
          {
            id: "model",
            kind: "model" as const,
            name: "Model",
            entities: [] as DrawingEntity[],
          },
        ];

  // MC3 — build viewportsBySpaceId from the routed spaces.
  // Scan each space's entity array for Viewport entities and group by spaceId.
  // Only paper-space layouts carry VIEWPORT entities in practice; model space
  // does not. The guard is inclusive so any unusual DXF structure still works.
  const viewportsBySpaceId: Record<string, ViewportEntity[]> = {};
  for (const space of finalSpaces) {
    const vps = space.entities.filter(
      (e): e is ViewportEntity => e.type === "Viewport",
    );
    if (vps.length > 0) {
      viewportsBySpaceId[space.id] = vps;
    }
  }

  // Block definitions pass through four stages:
  //   1. importBlockDefinitions     — parse BLOCKS section into drawing-model shape
  //   2. attachDynamicBlockMetadata — decode OBJECTS eval graph (DYN-1.4)
  //   3. detectDynamicBlocks        — set the boolean `dynamicBlock` flag on
  //                                    every block so the UI can distinguish
  //                                    dynamic masters from static blocks even
  //                                    when the eval graph lives beyond our
  //                                    current parser tier (Track A detection).
  //   4. extractDynamicBlockParams +
  //      attachInsertCustomProperties — derive per-block parameter lists and
  //                                    seed each INSERT's customProperties
  //                                    with the authored defaults, so the
  //                                    Properties palette has something to
  //                                    show for dynamic INSERTs. Closes
  //                                    TODO(dynamic-block-params).
  //
  // Stage 3 runs before stage 4 because param extraction leans on the
  // `dynamicBlock` flag to skip static blocks quickly.
  const rawBlocks = importBlockDefinitions(getBlocksSection(document));
  const blocksWithMetadata = attachDynamicBlockMetadata(
    rawBlocks,
    importBlockRecords(getBlocksSection(document), getTablesSection(document)),
    getObjectsSection(document),
    getTablesSection(document),
  );
  const blocks = detectDynamicBlocks(document, blocksWithMetadata);
  const paramDefinitions = extractDynamicBlockParams(document, blocks);
  attachInsertCustomProperties(document, finalSpaces, paramDefinitions);

  const objectsSection = getObjectsSection(document);

  // DYN-4 Phase 6 — DICTIONARY records are parsed into the structured
  // `section.dictionaries` bucket, but the drawing-model does not carry them
  // typed (owner/child bookkeeping walked at import time for ACAD_ENHANCEDBLOCK /
  // ACAD_EVALUATION_GRAPH pointers). The exporter emits `dictionaries: []`, so
  // without a verbatim passthrough every round-tripped DXF loses the
  // BLOCK_RECORD → eval_graph chain and dynamic-block metadata goes silent on
  // re-import. Capture each DICTIONARY's groups into `dictionaryGroups`.
  //
  // @codex (PR #551 review, "Keep extension dictionaries before array graph")
  // — this MUST be built and PREPENDED ahead of the sentinel-substituted
  // content below (`buildBridgeRawObjectGroups`'s output: the catch-all,
  // resolved SPATIAL_FILTER records, and — since N9 — resolved AcDbAssocArray
  // graph records). DICTIONARY carries no positional sentinel of its own —
  // unlike SPATIAL_FILTER / AcDbAssocArray, giving every DICTIONARY a full
  // positional sentinel would touch dxf-io's writer's UNCONDITIONAL, widely-
  // depended-upon top-of-function dictionary emission (MLEADERSTYLE root
  // dicts, ACAD_SCALELIST, block-eval dicts, root-dict hoisting, …) — a much
  // larger, higher-blast-radius change out of this slice's scope. Placing the
  // dictionary re-inject FIRST instead restores the pre-N9 relative order: an
  // associative array's own extension dictionary — the overwhelmingly common
  // real-world shape, an ARRAYRECT's ext-dict sitting immediately before its
  // ACDBASSOCNETWORK — again precedes the assoc-array graph in the exported
  // OBJECTS section, matching BOTH the true source order for that case AND
  // the pre-N9 established order (the OLD code appended assoc-array content
  // unconditionally at the absolute tail, AFTER this same dictionary
  // reinject). N9's sentinel substitution correctly interleaves the
  // assoc-array graph among UNRELATED catch-all records (the actual N9
  // deliverable) — this fix only restores dictionary→graph order, it does
  // NOT reopen or claim to fix DICTIONARY's position relative to OTHER
  // catch-all / SPATIAL_FILTER content, which remains un-guaranteed at the
  // bridge layer (a pre-existing, documented gap — see
  // `dxfBridge.spatialFilter.test.ts`'s "NOT a stable positional neighbour"
  // note — unrelated to N9 and not reopened by this fix).
  const dictionaryGroups: { code: number; value: string }[] = [];
  if (objectsSection && objectsSection.dictionaries.length > 0) {
    for (const dict of objectsSection.dictionaries) {
      // Batch-6 S-OBJECTS-DICT-ROUNDTRIP — preserve the parsed verbatim
      // fields (102/{ACAD_XDICTIONARY}, 102/{ACAD_REACTORS}, XData,
      // ACDBDICTIONARYWDFLT default-340) through the bridge re-synth so the
      // round-tripped DXF is group-faithful. Layout mirrors
      // serializeDictionaryEntry (dxf-io/writer.ts).
      dictionaryGroups.push({
        code: 0,
        value: dict.isWithDefault ? "ACDBDICTIONARYWDFLT" : "DICTIONARY",
      });
      if (dict.handle) dictionaryGroups.push({ code: 5, value: dict.handle });
      if (dict.extensionDictionaryHandle) {
        dictionaryGroups.push({ code: 102, value: "{ACAD_XDICTIONARY" });
        dictionaryGroups.push({
          code: 360,
          value: dict.extensionDictionaryHandle,
        });
        dictionaryGroups.push({ code: 102, value: "}" });
      }
      if (dict.reactorHandles && dict.reactorHandles.length > 0) {
        dictionaryGroups.push({ code: 102, value: "{ACAD_REACTORS" });
        for (const h of dict.reactorHandles) {
          dictionaryGroups.push({ code: 330, value: h });
        }
        dictionaryGroups.push({ code: 102, value: "}" });
      }
      if (dict.ownerHandle)
        dictionaryGroups.push({ code: 330, value: dict.ownerHandle });
      dictionaryGroups.push({ code: 100, value: "AcDbDictionary" });
      dictionaryGroups.push({
        code: 280,
        value: dict.hardOwned ? "1" : "0",
      });
      dictionaryGroups.push({ code: 281, value: String(dict.cloning) });
      for (const ref of dict.entries) {
        dictionaryGroups.push({ code: 3, value: ref.name });
        dictionaryGroups.push({
          code: ref.isHard ? 360 : 350,
          value: ref.handle,
        });
      }
      if (dict.isWithDefault || dict.defaultHandle !== undefined) {
        dictionaryGroups.push({
          code: 100,
          value: "AcDbDictionaryWithDefault",
        });
        if (dict.defaultHandle !== undefined) {
          dictionaryGroups.push({ code: 340, value: dict.defaultHandle });
        }
      }
      if (dict.extraGroups) {
        for (const g of dict.extraGroups) {
          dictionaryGroups.push({ code: g.code, value: g.value });
        }
      }
      if (dict.xdata) {
        for (const xd of dict.xdata) {
          dictionaryGroups.push({ code: 1001, value: xd.appName });
          for (const g of xd.groups) {
            dictionaryGroups.push({ code: g.code, value: g.value });
          }
        }
      }
    }
  }

  // E2 fidelity — preserve OBJECTS rawObjectGroups (MATERIAL / VISUALSTYLE /
  // XRECORD / SCALE / PLOTSETTINGS / GROUP / …), TABLES rawTableGroups (VPORT /
  // VIEW / UCS / APPID / …), and the HEADER / CLASSES / ACDSDATA /
  // THUMBNAILIMAGE raw sections — drawing-level metadata the bridge does not
  // model yet; dropping them breaks real-world round-trip (АБИ1.dxf has 500k+
  // raw object groups). They travel as opaque byte payloads, re-injected
  // verbatim. `dictionaryGroups` (above) is PREPENDED ahead of this
  // sentinel-substituted content — see that block's doc comment.
  const rawObjectGroups = [
    ...dictionaryGroups,
    ...(objectsSection ? buildBridgeRawObjectGroups(objectsSection) : []),
  ];

  // AW-0 foundation (docs/MASTERPLAN.md AW-0) — carry the AcDbAssocArray
  // OBJECTS graph through ADDITIONALLY as a TYPED carrier
  // (`rawAssocArrayObjects`), alongside (NOT instead of) the byte-position-
  // correct `rawObjectGroups` flatten (`buildBridgeRawObjectGroups`, above —
  // N9 sentinel substitution). The typed carrier exists purely as additional
  // structure for the dirty-diff choke point (dxfBridge.arrayAssocExport.ts)
  // and for AW-1's (params write-back) in-place `patchAssocArrayGraphRawGroups`
  // to locate + surgically patch a specific named ValueParam without
  // re-parsing an opaque byte stream — it is NEVER itself re-flattened into
  // `rawObjectGroups` (that would double-emit every array graph: once via the
  // sentinel substitution above, once here).
  const rawAssocArrayObjects: AssocArrayCarrierObject[] | undefined =
    objectsSection?.assocArrays?.length
      ? objectsSection.assocArrays
      : undefined;
  // N9 AW-0-OBJECTS-BUCKET-ORDER — CLOSED. Every AcDbAssocArray graph record
  // is now spliced into `rawObjectGroups` AT ITS TRUE SOURCE POSITION by
  // `buildBridgeRawObjectGroups`'s sentinel substitution (mirrors the
  // SPATIAL_FILTER precedent exactly — dxf-io's `ASSOC_ARRAY_PLACEHOLDER_CODE`,
  // see that constant's doc in packages/dxf-io/src/assocArrayPlaceholder.ts).
  // The OLD
  // behaviour here — re-appending every `assoc.rawGroups` AFTER the whole
  // `rawObjectGroups` catch-all — is GONE: it was byte-for-byte the pre-AW-0
  // baseline for dictionaries/dynamicBlockObjects, extended to assoc-arrays,
  // and it silently reordered every associative array in the OBJECTS section
  // relative to unrelated records (MATERIAL / XRECORD / GROUP / …) the parser
  // never pulled into a typed bucket. `dynamicBlockObjects` STILL uses that
  // append-at-tail pattern (out of N9's scope — see its own reinject site
  // below); `dictionaries` no longer does — see the `dictionaryGroups`
  // PREPEND above (Codex PR #551 review round, "Keep extension dictionaries
  // before array graph"). Doing this append here as well as the sentinel
  // substitution above would DOUBLE-EMIT every array graph — removed, not
  // merely made redundant.

  // DYN-4 Fix 1 — AutoCAD 2018+ un-prefixed `BLOCK*` classes now route
  // into `dynamicBlockObjects[]` instead of `rawObjectGroups`. The
  // export path (`exportDynamicBlockObjects`) only emits records that
  // belong to a block with populated `dynamicMetadata`. Any orphan
  // object — the eval-graph owners that aren't reachable from a known
  // BLOCK_RECORD handle, or records whose block didn't get typed
  // metadata — would otherwise be dropped (≈1.5 MB of АБИ1.dxf).
  // Capture every orphan's raw groups into `rawObjectGroups` so the
  // writer's passthrough re-injects them verbatim.
  if (objectsSection && objectsSection.dynamicBlockObjects.length > 0) {
    const claimedHandles = new Set<string>();
    for (const block of Object.values(blocks)) {
      const meta = block.dynamicMetadata;
      if (!meta) continue;
      // DYN-4 Phase 6 — blocks resolved via the ACAD_EVALUATION_GRAPH
      // chain (rather than the BLOCK / BLOCK_RECORD handle) carry
      // `futureExtensions.evalGraphSourced === true`. For those blocks
      // the writer skips typed synthesis (see `exportDynamicBlockObjects`),
      // so we must NOT claim their handles either — every raw record has
      // to survive via the passthrough dump below, keeping byte-perfect
      // round-trip for constraint-based АБИ1.dxf blocks.
      if (meta.futureExtensions?.evalGraphSourced === true) continue;
      // Every parameter / action id equals the DXF handle of the
      // source object (see parser.ts: `id: obj.handle`). Constraints
      // share the same rule. `futureExtensions.rawObjects` carries the
      // preserved DxfDynamicBlockObject[] directly.
      for (const p of meta.parameters) claimedHandles.add(p.id);
      for (const a of meta.actions) claimedHandles.add(a.id);
      for (const c of meta.geometricConstraints) claimedHandles.add(c.id);
      const preserved =
        (meta.futureExtensions.rawObjects as
          | { handle?: string }[]
          | undefined) ?? [];
      for (const obj of preserved) {
        if (obj?.handle) claimedHandles.add(obj.handle);
      }
    }
    for (const obj of objectsSection.dynamicBlockObjects) {
      if (claimedHandles.has(obj.handle)) continue;
      for (const g of obj.rawGroups) {
        rawObjectGroups.push({ code: g.code, value: g.value });
      }
    }
  }
  const tablesSectionForRaw = getTablesSection(document);
  const rawTableGroups = tablesSectionForRaw
    ? tablesSectionForRaw.rawTableGroups.map((g) => ({
        code: g.code,
        value: g.value,
      }))
    : [];
  const savedView = extractSavedViewFromRawTableGroups(rawTableGroups);
  const rawPassthroughSections: {
    name: string;
    groups: { code: number; value: string }[];
  }[] = [];
  for (const section of document.sections) {
    if (section.kind !== "raw-section") continue;
    // We only need the HEADER / CLASSES / ACDSDATA / THUMBNAILIMAGE kind
    // of raw section. BLOCKS and ENTITIES are already handled by their
    // structured counterparts, so even if the parser happens to emit a
    // legacy raw-section variant we skip them to avoid double-emission.
    if (
      section.name === "BLOCKS" ||
      section.name === "ENTITIES" ||
      section.name === "TABLES" ||
      section.name === "OBJECTS"
    ) {
      continue;
    }
    rawPassthroughSections.push({
      name: section.name,
      groups: section.groups.map((g) => ({ code: g.code, value: g.value })),
    });
  }

  // ── PR-3 — annotative scale wire-up ───────────────────────────────
  //
  // Three-step wiring:
  //   (a) HEADER vars: read $CANNOSCALE / $CANNOSCALEVALUE /
  //       $ANNOALLVISIBLE / $ANNOAUTOSCALE / $ANNOMONITOR off the raw
  //       HEADER section and surface them on the document header.
  //   (b) `annotationScales` catalogue: lift `AcDbScale` records from
  //       OBJECTS into a flat `[{name, scaleFactor}]` list used by the
  //       status-bar dropdown and renderEntity validation.
  //   (c) Per-entity `annotativeContexts`: walk
  //       `resolveAnnotativeContexts(...).entityContexts` and attach
  //       the matching contexts to each TEXT/MTEXT/HATCH/INSERT in the
  //       finalSpaces list. Entity ids equal DXF handles when present
  //       (see dxfBridge.spaces.ts:256), so the lookup is direct.
  //
  // DIM/MLEADER are intentionally skipped — they use the legacy
  // `annotativeContextSchema` (model.ts) and are migrated in PR-7.

  // (a) Read annotative HEADER variables off the raw HEADER section.
  const headerSection = rawPassthroughSections.find((s) => s.name === "HEADER");
  const annoVars = headerSection
    ? readAnnotativeVarsFromHeader(headerSection.groups)
    : {};

  // PDF-6.1 — typed linetype-scale HEADER vars ($LTSCALE / $CELTSCALE /
  // $PSLTSCALE / $MSLTSCALE). Surfaced as typed document fields (in addition
  // to the verbatim rawPassthroughSections copy) so the PDF dash emitter can
  // read them without re-scanning the raw header.
  const ltVars = headerSection
    ? readLinetypeVarsFromHeader(headerSection.groups)
    : {};

  // Batch-6 — document-level HEADER vars ($INSUNITS / $PDMODE / $PDSIZE).
  // Lifted into typed document fields (in addition to the verbatim
  // rawPassthroughSections copy). $INSUNITS drives INSERT auto-scale
  // (blockMutations.ts); $PDMODE/$PDSIZE are surfaced for the point-marker
  // render arm (DEFUSE-owned).
  const docVars = headerSection
    ? readDocumentVarsFromHeader(headerSection.groups)
    : {};

  // $INSBASE — the drawing insertion base point (AutoCAD BASE command).
  // Lifted into the typed `doc.insertionBase` field (group codes 10/20/30)
  // in addition to the verbatim rawPassthroughSections copy. Round-trip
  // faithfulness on EXPORT is handled by `patchPointVarsIntoHeaderGroups`.
  const pointVars = headerSection
    ? readPointVarsFromHeader(headerSection.groups)
    : {};

  // OSNAP-HEADER-ROUNDTRIP — running object-snap HEADER var ($OSMODE).
  // Lifted into the typed `doc.osmode` field (in addition to the verbatim
  // rawPassthroughSections copy) so the drawing-file running-osnap bitcode is
  // preserved across import→export. $APERTURE / $AUTOSNAP are runtime-only
  // sysvars (not DXF header vars) and are intentionally not read here.
  const snapVars = headerSection
    ? readSnapVarsFromHeader(headerSection.groups)
    : {};

  // (b) Build the annotationScales catalogue from OBJECTS.acadScales.
  const annotationScales: { name: string; scaleFactor: number }[] = [];
  if (objectsSection?.acadScales) {
    for (const s of objectsSection.acadScales) {
      // Skip hidden scales (AutoCAD does not expose them in the dropdown).
      if (s.isHidden) continue;
      // Skip duplicates by name — first-wins matches AutoCAD's
      // behaviour when the SCALELIST has trailing legacy entries.
      if (annotationScales.some((x) => x.name === s.name)) continue;
      annotationScales.push({
        name: s.name,
        scaleFactor: s.scaleFactor,
      });
    }
  }

  // ── Import diagnostics — orphan/dangling-reference telemetry ──
  //
  // The three OBJECTS-section second-pass resolvers (annotative contexts, assoc
  // arrays, viewport clip) each count references whose handle chain dangled
  // ("surfaced for audit-log telemetry" per their doc-comments). Accumulate
  // every count here and project the non-zero ones onto the document's
  // `importDiagnostics` field so the audit log / UI can report them — the
  // resolvers themselves never throw and the raw records still round-trip
  // verbatim. (EPIC-VIEWPORT-CLIP SLICE 3 FINDING 3 — the viewport-clip count
  // shares this path with the annotative + assoc counts.)
  let orphanAnnotativeScaleReferences = 0;
  let orphanAssocArrayReferences = 0;
  let orphanViewportClipReferences = 0;

  // (c) Per-entity annotativeContexts — second-pass resolver.
  if (objectsSection && objectsSection.annotScaleContexts?.length) {
    const allTopLevel = getSupportedEntities(document);
    // Include block-children too — annotative entities can live inside
    // BLOCKs (e.g. annotative TEXT in a *Model_Space block).
    const blocksSectionForResolve = getBlocksSection(document);
    const allEntities = [...allTopLevel];
    if (blocksSectionForResolve) {
      for (const block of blocksSectionForResolve.blocks) {
        for (const child of block.entities) {
          if (child.kind === "supported-entity") {
            allEntities.push(child);
          }
        }
      }
    }
    const resolution = resolveAnnotativeContexts(objectsSection, allEntities);
    orphanAnnotativeScaleReferences = resolution.orphanScaleReferences;
    if (resolution.entityContexts.size > 0) {
      // Apply contexts to drawing-model entities by id (= DXF handle).
      // We mutate in place since `finalSpaces` is freshly built and not
      // exposed yet — Zod parse downstream re-validates.
      for (const space of finalSpaces) {
        for (const entity of space.entities) {
          const handleId = entity.id;
          const contexts = resolution.entityContexts.get(handleId);
          if (!contexts || contexts.length === 0) continue;
          // Only TEXT/MTEXT/HATCH/INSERT carry annotativeContexts on
          // their schema in PR-1/PR-3. Other entity types skip.
          if (
            entity.type === "Text" ||
            entity.type === "MText" ||
            entity.type === "Hatch" ||
            entity.type === "Block"
          ) {
            // Use a TS cast — the dxf-io AnnotScaleContext shape and the
            // drawing-model AnnotScaleContext shape are structurally
            // identical (same `kind` discriminator + same field set).
            // Zod's strip mode drops the optional `rawGroups` field on
            // downstream `drawingDocumentSchema.parse(...)`.
            (
              entity as unknown as { annotativeContexts: AnnotScaleContext[] }
            ).annotativeContexts = contexts as unknown as AnnotScaleContext[];
          }
        }
      }
      // Also walk block definitions so annotative children inside
      // BLOCK records receive their per-scale contexts.
      for (const blockDef of Object.values(blocks)) {
        for (const child of blockDef.entities) {
          const childAny = child as unknown as { id?: string; type: string };
          if (!childAny.id) continue;
          const contexts = resolution.entityContexts.get(childAny.id);
          if (!contexts || contexts.length === 0) continue;
          // Batch-7 admitted HATCH, and S10d round-19 (@codex,
          // attdefFromEntity:720) admits ATTDEF: both carry the SAME
          // `annotScaleContextSchema` union as TEXT/MTEXT/INSERT (model.ts), so
          // they join the direct-cast path. Without ATTDEF here an annotative
          // block ATTDEF's per-scale contexts stay `[]`, so `attdefFromEntity`'s
          // resolver renders the ATTRIB at its master position and never hides
          // it at unsupported scales. DIMENSION/MULTILEADER are EXCLUDED:
          // MULTILEADER receives its contexts inline via `g.annotativeContexts`
          // (the resolver is not its source), and DIMENSION's field still uses
          // the LEGACY `annotativeContextSchema` shape — structurally
          // INCOMPATIBLE with the resolver's `kind:"dimension"` member; its
          // decode is a deferred subsystem (PR-2/PR-7). See annotative.ts §7.
          if (
            child.type === "Text" ||
            child.type === "MText" ||
            child.type === "Hatch" ||
            child.type === "Block" ||
            child.type === "Attdef"
          ) {
            (
              child as unknown as { annotativeContexts: AnnotScaleContext[] }
            ).annotativeContexts = contexts as unknown as AnnotScaleContext[];
          }
        }
      }
    }
  }

  // ── Batch-8 S-ARRAYASSOC-RESOLVE — associative-array second pass ──
  //
  // Join each ARRAY entity to its AcDbAssocArray OBJECTS graph (ext-dict →
  // action body → rectangular params + AcDbAssocDependency source FKs) and
  // attach the typed §9.4 params + sourceEntityIds + handle linkage onto the
  // model entity. Mirrors the annotativeContexts second pass above.
  if (objectsSection?.assocArrays?.length) {
    const allTopLevel = getSupportedEntities(document);
    const blocksForAssoc = getBlocksSection(document);
    const allEntities = [...allTopLevel];
    if (blocksForAssoc) {
      for (const block of blocksForAssoc.blocks) {
        for (const child of block.entities) {
          if (child.kind === "supported-entity") allEntities.push(child);
        }
      }
    }
    const assocResolution = resolveAssocArrays(objectsSection, allEntities);
    orphanAssocArrayReferences = assocResolution.orphanDependencyReferences;
    if (assocResolution.byEntityHandle.size > 0) {
      // Source-grid presence lookup (Codex #251-B follow-up): the model
      // entity's spacing is a required number, so the parser's presence-aware
      // optionality (group 44/45 absent ⇒ `undefined`) is collapsed to 0 by
      // the time `applyAssocArrayResolution` sees it. Hand the resolver the
      // PRE-collapse dxf-io geometry so it can tell a real explicit `44 0`
      // (preserve) from an absent group (eligible for the producer default).
      const sourceGridByHandle = new Map<
        string,
        DxfArrayRectEntity["geometry"]
      >();
      for (const dxfEntity of allEntities) {
        if (dxfEntity.type !== "ARRAYRECT") continue;
        const handle = dxfEntity.common.handle;
        if (handle !== undefined) {
          sourceGridByHandle.set(handle, dxfEntity.geometry);
        }
      }
      for (const space of finalSpaces) {
        for (const entity of space.entities) {
          if (entity.type !== "Rectangular Associative Array") continue;
          const resolved = assocResolution.byEntityHandle.get(entity.id);
          if (resolved) {
            applyAssocArrayResolution(
              entity,
              resolved,
              sourceGridByHandle.get(entity.id),
            );
          }
        }
      }
    }
  }

  // ── EPIC-VIEWPORT-CLIP SLICE 3 — viewport clip + membership second pass ──
  //
  // Two typed projections onto each paper-space VIEWPORT model entity, wired
  // here next to the annotative / assoc-array second passes:
  //   (1) `clipPath` — resolveViewportClip joins each VIEWPORT (via its DXF
  //       extension-dictionary → ACAD_FILTER → SPATIAL → SPATIAL_FILTER chain)
  //       to its clip boundary, transformed to WCS by the inverse-insert matrix.
  //   (2) `childEntityIds` — buildViewportMembershipIndex computes, per VIEWPORT,
  //       the model-space entity ids whose geometry falls inside the viewport's
  //       model-space view rectangle.
  // Both are optional typed object-graphs; absent ⇒ no clip / not computed.
  //
  // Extracted into the SHARED `applyViewportClipAndMembership` so the SAME pass
  // runs on the async/worker STREAMING import path too (full-fidelity import,
  // ПРИОРИТЕТ #1) — see that function's header for the per-path input split. The
  // synchronous bridge has BOTH inputs in hand: the raw OBJECTS section + the raw
  // top-level/block-child VIEWPORT entities (clip) and the model-space typed
  // entities (membership), so it runs both halves here in one shot.
  //
  // Clip viewports: the DXF VIEWPORT entities carry
  // `common.extensionDictionaryHandle` — the anchor the clip resolver needs.
  // Top-level ENTITIES + block children (mirrors the annotative pass) so a
  // clipped viewport inside a paper-space block still resolves. This is the
  // pre-drain (sync) source of truth; the shared fn falls back to a typed-shim
  // when these are absent (drained streaming path).
  const docForClipFinalize: DrawingDocument = {
    spaces: finalSpaces,
  } as unknown as DrawingDocument;
  // Collect the raw VIEWPORT entities for the clip resolver from the (pre-drain)
  // top-level ENTITIES + block children. On the STREAMING worker path the
  // top-level array was drained before this point, so this can be empty OR carry
  // ONLY block-definition VIEWPORTs.
  //
  // FILTER to raw VIEWPORTs only (Codex #326 P2): the resolver consumes ONLY
  // VIEWPORT entities (it `continue`s on every other `type`). We ALWAYS pass the
  // raw VIEWPORT list (when non-empty) — the shared fn now MERGES it with typed
  // shims for any retained paper viewport the raw list is missing (Codex #326 r3
  // P2 #2), so a drained-streaming raw list containing ONLY a block-definition
  // VIEWPORT no longer suppresses resolution of the retained TOP-LEVEL paper
  // viewport. When the raw list is empty, omit the option so the fn builds the
  // shims alone (the prior fallback). The merge dedups by handle, raw winning.
  let clipViewports: DxfSupportedEntity[] | undefined;
  {
    const allTopLevel = getSupportedEntities(document);
    const blocksForClip = getBlocksSection(document);
    const allDxfEntities = [...allTopLevel];
    if (blocksForClip) {
      for (const block of blocksForClip.blocks) {
        for (const child of block.entities) {
          if (child.kind === "supported-entity") allDxfEntities.push(child);
        }
      }
    }
    const rawViewportsOnly = allDxfEntities.filter(
      (e) => e.type === "VIEWPORT",
    );
    clipViewports =
      rawViewportsOnly.length > 0 ? rawViewportsOnly : undefined;
  }
  // Streaming detection: under `discardEntitiesAfterStreaming` the paper-space
  // boundary entity referenced by a native group-340 VPCLIP is streamed out and
  // not retained in the bridge shell, so the native-340 boundary lookup would
  // spuriously fail here and over-count orphans. SKIP that half in the worker;
  // the MAIN thread re-runs ONLY the native-340 half afterwards from the compact
  // cache (Codex #326 P2 — worker:372). The SPATIAL_FILTER half still resolves
  // here (it reads the OBJECTS section, intact in the worker). The non-streaming
  // bridge runs both halves in one shot.
  const isStreaming = options.discardEntitiesAfterStreaming === true;
  const clipFinalize = applyViewportClipAndMembership(
    docForClipFinalize,
    objectsSection,
    blocks,
    {
      ...(clipViewports ? { clipViewports } : {}),
      ...(isStreaming ? { skipNativeBoundaryClip: true } : {}),
    },
  );
  orphanViewportClipReferences = clipFinalize.orphanViewportClipReferences;
  const resolvedStyleTables = importStyleTables(document); // EXPORT-VP-FROZEN
  // 3rd arg = PRE-DRAIN authoritative-XDATA handle set (@codex #486 P2 —
  // never the post-drain document, which is empty on the streaming path).
  resolveImportedVpFrozen(finalSpaces, resolvedStyleTables.layers, xdataAuthoritativeVpHandles);
  // Assemble import diagnostics — emit only the non-zero orphan counts so a
  // clean import carries no `importDiagnostics` field (compact snapshots).
  const importDiagnostics: {
    orphanAnnotativeScaleReferences?: number;
    orphanAssocArrayReferences?: number;
    orphanViewportClipReferences?: number;
  } = {};
  if (orphanAnnotativeScaleReferences > 0)
    importDiagnostics.orphanAnnotativeScaleReferences =
      orphanAnnotativeScaleReferences;
  if (orphanAssocArrayReferences > 0)
    importDiagnostics.orphanAssocArrayReferences = orphanAssocArrayReferences;
  if (orphanViewportClipReferences > 0)
    importDiagnostics.orphanViewportClipReferences =
      orphanViewportClipReferences;
  const hasImportDiagnostics = Object.keys(importDiagnostics).length > 0;

  // Resolve cannoscale fields. Order: explicit HEADER value → first
  // catalogue entry → schema default ("1:1" / 1).
  // An ABSENT $CANNOSCALE surfaces as an EMPTY string (not nullish), so the
  // `??` fallback chain would NOT fall through and the import would explicitly
  // set `cannoscaleName: ""` — overriding the schema default "1:1" and making
  // the reference audit flag the (required) cannoscaleName as empty/missing
  // (UNDEFINED_ANNOTATION_SCALE). The assembled import document is NOT re-parsed
  // through `drawingDocumentSchema`, so the schema `.default("1:1")` never
  // applies here; coerce an empty/absent value all the way down to the explicit
  // conformant "1:1" — AutoCAD's always-present built-in annotation scale (the
  // reference audit always seeds "1:1" into the resolvable set, so it resolves
  // even with an empty SCALELIST). A real $CANNOSCALE / catalogue entry still
  // wins.
  const cannoscaleNameResolved =
    (annoVars.cannoscaleName && annoVars.cannoscaleName.length > 0
      ? annoVars.cannoscaleName
      : undefined) ??
    annotationScales[0]?.name ??
    "1:1";
  const cannoscaleResolved =
    annoVars.cannoscaleValue !== undefined
      ? // AcDbScale stores `paperUnits/drawingUnits` (e.g. 0.02 for
        // 1:50). Header `cannoscale` follows the same convention.
        annoVars.cannoscaleValue
      : (annotationScales.find((s) => s.name === cannoscaleNameResolved)
          ?.scaleFactor ??
        // BUG 1 (round-12 P2) — name/factor consistency. When neither a
        // $CANNOSCALE value NOR a catalogue entry resolves the factor and the
        // NAME fell all the way back to the built-in conformant "1:1" (no
        // $CANNOSCALE + empty SCALELIST), pair it with the matching 1:1 RATIO
        // factor of 1. Leaving it `undefined` desyncs name⇔factor — the doc
        // would carry cannoscaleName="1:1" with no cannoscale value. A real
        // $CANNOSCALE / catalogue entry still wins above; this only fills the
        // default-name case so name + factor stay consistent.
        (cannoscaleNameResolved === "1:1" ? 1 : undefined));

  return {
    id: documentId,
    title,
    // DXF-CONFORMANT-SKELETON — mark this document as imported from a DXF file
    // so `ensureConformantSkeleton` passes it through unchanged (byte-faithful
    // carry-forward) instead of back-filling the skeleton + re-minting handles.
    // In-memory hint only; never serialized to the exported DXF bytes.
    provenance: "imported" as const,
    activeSpaceId:
      finalSpaces.find((space) => space.kind === "model")?.id ??
      finalSpaces[0]!.id,
    spaces: finalSpaces,
    styleTables: resolvedStyleTables,
    blocks,
    blockRecords: importBlockRecords(
      getBlocksSection(document),
      getTablesSection(document),
    ),
    // PR-LT2 — parse the LTYPE table into typed records and MERGE them over
    // the standard acad.lin seed (custom wins by case-insensitive name). The
    // `drawingDocumentSchema.linetypes` preprocess/default only seeds on
    // `.parse()`, but the prod worker/client path (`assembleDrawingDocument`)
    // is an identity cast that never runs the Zod walk — so the merge is done
    // here explicitly. `importLinetypes` returns a fresh deep copy of the seed
    // for the absent / empty-LTYPE case, keeping each imported document's table
    // independent. The file's raw 40 / 73 are preserved (patternLengthRaw /
    // elementCountRaw) so a plain round-trip re-emits them byte-exact (§10.3).
    linetypes: importLinetypes(
      tablesSectionForRaw?.linetypes ?? [],
      tablesSectionForRaw?.textStyles ?? [],
    ),
    // FIX-3 — persist the LTYPE table-LEVEL header (table handle / owner /
    // subclass / max-entries) on the document so a pure MODEL round-trip
    // (where the transient parsed section is gone) still re-emits the real
    // header verbatim instead of a minimally-synthesised one (Codex#2 P2).
    ...(tablesSectionForRaw?.linetypeTableHeader &&
    tablesSectionForRaw.linetypeTableHeader.length > 0
      ? {
          linetypeTableHeader: tablesSectionForRaw.linetypeTableHeader.map(
            (g) => ({ code: g.code, value: g.value }),
          ),
        }
      : {}),
    // FIX-110 — persist the LAYER / STYLE / DIMSTYLE / BLOCK_RECORD table heads
    // so a pure MODEL round-trip re-emits the real `5/<handle>` head (the
    // transient parsed section is gone by export time), mirroring the LTYPE
    // header persistence above. The four typed-table writers previously emitted
    // no head → ezdxf silently repaired it → AutoCAD recovery.
    ...(tablesSectionForRaw?.symbolTableHeaders &&
    Object.keys(tablesSectionForRaw.symbolTableHeaders).length > 0
      ? {
          symbolTableHeaders: Object.fromEntries(
            Object.entries(tablesSectionForRaw.symbolTableHeaders).map(
              ([name, head]) => [
                name,
                head.map((g) => ({ code: g.code, value: g.value })),
              ],
            ),
          ),
        }
      : {}),
    layouts: importLayouts(getObjectsSection(document)),
    layoutsBySpaceId,
    viewportsBySpaceId,
    // Batch-6 — $INSUNITS / $PDMODE / $PDSIZE lifted from the HEADER. ALWAYS
    // set a valid number (default 0 when the $-var is absent) so the import
    // result is a complete DrawingDocument whose schema invariant
    // (`insunits`/`pdmode`/`pdsize` are numbers) holds even on the prod
    // identity-cast path that skips the Zod walk. Leaving them `undefined`
    // breaks the store merge in storeHelpers (`current === 0 && imported !== 0`
    // → `undefined !== 0` is true → a unitless import overwrites a valid 0 with
    // undefined). Round-trip faithfulness is preserved on the EXPORT side by the
    // patch step (each spec's `defaultValue: 0`): an absent-in-source var is
    // appended only when non-zero, while a var already present in the HEADER is
    // overwritten in-place with the current value (including 0) so edits back to
    // 0 persist (Codex#248 F1 round-2).
    insunits: docVars.insunits ?? 0,
    // $INSBASE — drawing insertion base point. ALWAYS materialised (default
    // (0,0,0) when the $-var is absent) so the import result is a complete
    // DrawingDocument even on the prod identity-cast path that skips the Zod
    // walk — leaving it undefined would break consumers that read
    // `doc.insertionBase`. Round-trip faithfulness is preserved on EXPORT:
    // `collectPointVars` + `patchPointVarsIntoHeaderGroups` only emit a
    // non-default base (an absent-in-source (0,0,0) is never invented), while
    // an edit (incl. back to (0,0,0) on an existing var) overwrites in place.
    insertionBase: pointVars.insertionBase ?? { x: 0, y: 0, z: 0 },
    pdmode: docVars.pdmode ?? 0,
    pdsize: docVars.pdsize ?? 0,
    // $MIRRTEXT — MIRROR text-handling switch. ALWAYS materialised (default 0 —
    // AutoCAD's default — when the $-var is absent) so the import result is a
    // complete DrawingDocument even on the prod identity-cast path that skips
    // the Zod walk. Round-trip faithfulness lives on EXPORT (collectDocumentVars
    // + the patch step, spec `defaultValue: 0`): a default-0 absent source is
    // never invented, while a present var is overwritten in-place so an edit
    // back to 0 persists.
    //
    // CLAMP to 0|1 (Codex #342 F4): AutoCAD's MIRRTEXT is STRICTLY a boolean
    // toggle — any non-zero value is treated as 1. A DXF carrying an out-of-range
    // `$MIRRTEXT=2` therefore lands as 1 here. This MUST be clamped at the bridge
    // (not only in the model Zod schema's `.transform`) because the prod import
    // builds the document on an IDENTITY-CAST path that skips the Zod walk — so
    // the raw 2 would otherwise survive and (a) be rejected by the live sysvar
    // validator on hydration (`isValidSysvarValue` accepts only 0|1, leaving the
    // store stale) and (b) be re-emitted as an invalid `$MIRRTEXT 2` on export.
    // Clamp-non-zero→1 (vs reset-to-0) preserves the "mirror was on" intent.
    mirrtext: clampMirrtext(docVars.mirrtext),
    // $FILLMODE — the solid-fill display switch (hatches and fills, 2D solids
    // and WIDE POLYLINES). ALWAYS materialised so the import result is a
    // complete DrawingDocument even on the prod identity-cast path that skips
    // the Zod walk; an ABSENT var materialises `DEFAULT_FILLMODE` = 1, the
    // Autodesk documented INITIAL VALUE (and ezdxf `headervars.py`'s default
    // for the same group-70 var) — NOT 0, which is the real "outlines only"
    // state. Clamped to 0|1 here for the same reason `$MIRRTEXT` is: the
    // identity-cast path never runs the schema `.transform`, so a raw
    // `$FILLMODE=2` would otherwise survive into the renderer and the export.
    // Round-trip faithfulness lives on EXPORT (collectDocumentVars + the patch
    // step, spec `defaultValue: 1`): a default-1 absent source is never
    // invented a `$FILLMODE`, while a present var is overwritten in place so
    // an edit back to 1 persists.
    fillmode: clampFillmode(docVars.fillmode),
    // $CECOLOR — current-entity-colour sysvar. ALWAYS materialised (default
    // ByLayer when the $-var is absent — `cecolorFromHeader(undefined)` → byLayer)
    // so the import result is a complete DrawingDocument even on the prod
    // identity-cast path that skips the Zod walk; leaving it undefined would break
    // consumers reading `doc.cecolor`. The HEADER scalar packs the whole
    // EntityColor union into ONE int (256 = ByLayer; 0 = ByBlock; 1..255 = ACI;
    // negative C2 = true colour), decoded here. Round-trip faithfulness lives on
    // EXPORT (`collectDocumentVars` → `cecolorToHeader`, spec `defaultValue: 256`):
    // a standalone ByLayer is never invented, while a present $CECOLOR is
    // overwritten in-place so an edit back to ByLayer persists.
    cecolor: cecolorFromHeader(docVars.cecolor),
    // OSNAP-HEADER-ROUNDTRIP — running object-snap bitcode ($OSMODE). ALWAYS
    // materialised (default 4133 — AutoCAD's out-of-the-box value — when the
    // $-var is absent) so the import result is a complete DrawingDocument even
    // on the prod identity-cast path that skips the Zod walk; leaving it
    // undefined would break consumers reading `doc.osmode`. Round-trip
    // faithfulness lives on EXPORT: `collectSnapVars` + the patch step (spec
    // `defaultValue: 4133`) only append a non-default value (an absent-in-
    // source default is never invented), while a var already present in the
    // HEADER is overwritten in-place so edits — including back to 4133 —
    // persist.
    osmode: snapVars.osmode ?? 4133,
    // PDF-6.1 — typed linetype-scale sysvars (omit when absent so the schema
    // defaults of 1 apply; emitted only when the HEADER carried a value).
    ...(ltVars.ltscale !== undefined ? { ltscale: ltVars.ltscale } : {}),
    ...(ltVars.celtscale !== undefined ? { celtscale: ltVars.celtscale } : {}),
    ...(ltVars.psltscale !== undefined ? { psltscale: ltVars.psltscale } : {}),
    ...(ltVars.msltscale !== undefined ? { msltscale: ltVars.msltscale } : {}),
    // PR-3 — annotative HEADER vars + scale catalogue.
    ...(cannoscaleNameResolved !== undefined
      ? { cannoscaleName: cannoscaleNameResolved }
      : {}),
    ...(cannoscaleResolved !== undefined
      ? { cannoscale: cannoscaleResolved }
      : {}),
    ...(annotationScales.length > 0 ? { annotationScales } : {}),
    ...(annoVars.annoAllVisible !== undefined
      ? { annoAllVisible: annoVars.annoAllVisible }
      : {}),
    ...(annoVars.annoAutoScale !== undefined
      ? { annoAutoScale: annoVars.annoAutoScale }
      : {}),
    ...(annoVars.annoMonitor !== undefined
      ? { annoMonitor: annoVars.annoMonitor }
      : {}),
    // Emit the raw passthroughs only when non-empty so DrawingDocument
    // snapshots for simple fixtures stay compact and the optional schema
    // shape matches real-world usage.
    ...(rawEntityGroups.length > 0 ? { rawEntityGroups } : {}),
    ...(rawObjectGroups.length > 0 ? { rawObjectGroups } : {}),
    ...(rawAssocArrayObjects ? { rawAssocArrayObjects } : {}),
    ...(rawTableGroups.length > 0 ? { rawTableGroups } : {}),
    ...(rawPassthroughSections.length > 0 ? { rawPassthroughSections } : {}),
    ...(savedView !== undefined ? { savedView } : {}),
    ...(hasImportDiagnostics ? { importDiagnostics } : {}),
  };
}

/**
 * Walk the ENTITIES section of a parsed DxfDocument and capture every
 * unsupported-entity record as a RawEntityGroup. The parser writes
 * everything it cannot map onto a typed DxfSupportedEntity into
 * `{kind: "unsupported-entity", type, groups}`; here we strip the
 * `{code: 0, value: TYPE}` header (stored separately as `entityType`)
 * and keep the remaining groups in original order for verbatim re-emission.
 *
 * Block-scoped unsupported children are captured inside
 * `dxfBlockDefinitionToDrawingBlock` instead — this helper only deals
 * with the top-level ENTITIES section.
 */
/**
 * Walk the raw TABLES groups (as captured by the parser into
 * `DxfTablesSection.rawTableGroups`) and extract the saved view stored
 * on the VPORT `*Active` record.
 *
 * AutoCAD's DXF reference defines several point fields on a VPORT
 * record. The ones we want are the *view* center / height / aspect,
 * not the viewport rect corners — i.e. group codes 12/22 (view center
 * in DCS — see DCS-vs-WCS guard below), 40 (view height in drawing
 * units), 41 (aspect ratio width/height), and optionally 17/27/37 (view
 * target) and 16/26/36 (view direction). Codes 10/20 and 11/21 are the
 * viewport rectangle in screen coordinates (always (0,0) / (1,1) for
 * `*Active`) and are deliberately ignored.
 *
 * DCS-vs-WCS conversion: codes 12/22 are expressed in **DCS** (Display
 * Coordinate System), not WCS. For a plan view (viewDirection ≈ (0,0,1)
 * AND twist ≈ 0) DCS is the WCS XY plane translated by the view target
 * (codes 17/27/37) — so WCS center = target.xy + dcs.xy. Non-zero target
 * is a normal pan/elevation in plan views, NOT a reason to reject the
 * saved view. For oblique viewDirection or non-zero twist DCS axes no
 * longer align with WCS XY, so we conservatively return `undefined` and
 * the caller falls back to zoom-extents.
 *
 * Matching `*Active` is **case-insensitive** — some emitters write
 * `*ACTIVE` even though AutoCAD's canonical form is `*Active`.
 *
 * Returns `undefined` when no `*Active` VPORT is present or when any
 * required field (center.x/y, height, aspectRatio) is missing or
 * malformed. Never throws.
 */
export function extractSavedViewFromRawTableGroups(
  rawTableGroups: ReadonlyArray<{ code: number; value: string }>,
): SavedView | undefined {
  try {
    // Find every VPORT record (code 0, value "VPORT") and within each,
    // check whether group 2 = "*Active". The record runs until the next
    // code-0 group or the array end.
    const len = rawTableGroups.length;
    let i = 0;
    while (i < len) {
      const g = rawTableGroups[i]!;
      if (g.code !== 0 || g.value !== "VPORT") {
        i += 1;
        continue;
      }
      // Find the record's end (next code 0 or array end).
      let end = i + 1;
      while (end < len && rawTableGroups[end]!.code !== 0) {
        end += 1;
      }
      // Determine if this is the *Active record by scanning for group 2.
      // Case-insensitive: some DXF emitters write `*ACTIVE`.
      let isActive = false;
      for (let j = i + 1; j < end; j += 1) {
        const r = rawTableGroups[j]!;
        if (r.code === 2) {
          isActive = r.value.toLowerCase() === "*active";
          break;
        }
      }
      if (!isActive) {
        i = end;
        continue;
      }
      let centerX: number | undefined;
      let centerY: number | undefined;
      let height: number | undefined;
      let aspectRatio: number | undefined;
      // Per-axis defaults: target → (0,0,0); viewDirection → (0,0,1)
      // identity. Partial tuples (e.g. only code 17 emitted, 27/37
      // omitted) must NOT silently drop the emitted component; instead
      // each axis defaults to its identity value independently and the
      // origin/identity guard runs on the normalized triple.
      let targetX = 0;
      let targetY = 0;
      let targetZ = 0;
      let viewDirX = 0;
      let viewDirY = 0;
      let viewDirZ = 1;
      // VPORT twist angle (degrees). Absent ≡ 0. Non-zero twist rotates
      // DCS within the XY plane, so codes 12/22 are no longer WCS XY.
      let twist = 0;
      // Field-emission flags — kept separate from the guard logic. The
      // guard runs on the normalized triples (above). These flags only
      // decide whether to copy the parsed target / viewDirection onto
      // the returned SavedView, so consumers can distinguish "DXF
      // explicitly emitted identity" from "DXF omitted these fields".
      let anyTargetGroupSeen = false;
      let anyViewDirGroupSeen = false;
      for (let j = i + 1; j < end; j += 1) {
        const r = rawTableGroups[j]!;
        switch (r.code) {
          case 12: {
            const n = Number(r.value);
            if (Number.isFinite(n)) centerX = n;
            break;
          }
          case 22: {
            const n = Number(r.value);
            if (Number.isFinite(n)) centerY = n;
            break;
          }
          case 40: {
            const n = Number(r.value);
            if (Number.isFinite(n)) height = n;
            break;
          }
          case 41: {
            const n = Number(r.value);
            if (Number.isFinite(n)) aspectRatio = n;
            break;
          }
          case 17: {
            const n = Number(r.value);
            if (Number.isFinite(n)) {
              targetX = n;
              anyTargetGroupSeen = true;
            }
            break;
          }
          case 27: {
            const n = Number(r.value);
            if (Number.isFinite(n)) {
              targetY = n;
              anyTargetGroupSeen = true;
            }
            break;
          }
          case 37: {
            const n = Number(r.value);
            if (Number.isFinite(n)) {
              targetZ = n;
              anyTargetGroupSeen = true;
            }
            break;
          }
          case 16: {
            const n = Number(r.value);
            if (Number.isFinite(n)) {
              viewDirX = n;
              anyViewDirGroupSeen = true;
            }
            break;
          }
          case 26: {
            const n = Number(r.value);
            if (Number.isFinite(n)) {
              viewDirY = n;
              anyViewDirGroupSeen = true;
            }
            break;
          }
          case 36: {
            const n = Number(r.value);
            if (Number.isFinite(n)) {
              viewDirZ = n;
              anyViewDirGroupSeen = true;
            }
            break;
          }
          case 51: {
            const n = Number(r.value);
            if (Number.isFinite(n)) twist = n;
            break;
          }
          default:
            break;
        }
      }
      // Note: VPORT TABLE *Active is always a model-space saved view.
      // Paper-space "viewports" are 0/VIEWPORT entities in paperspace
      // blocks (OBJECTS/ENTITIES), not TABLES.VPORT records. Earlier
      // code mis-treated `code 70 bit 1` as a paper-space flag — that
      // bit is a symbol-table standard flag with different semantics.
      // No paper-space rejection here.
      if (
        centerX === undefined ||
        centerY === undefined ||
        height === undefined ||
        aspectRatio === undefined ||
        !(height > 0) ||
        !(aspectRatio > 0)
      ) {
        return undefined;
      }
      // DCS → WCS: codes 12/22 are the view centre in the DISPLAY
      // coordinate system, whose origin sits at the view TARGET
      // (17/27/37, in WCS). For an untwisted plan view the DCS axes are
      // parallel to the WCS axes, so the WCS centre is the per-axis sum.
      //
      // This was briefly changed to return the raw 12/22 pair, on the
      // strength of an ezdxf reading and a containment measurement. Both
      // arguments were bad and the change is reverted:
      //
      //   * ezdxf's `Document.set_modelspace_vport` writes `center` into
      //     12/22 and then `reset_wcs()`, which zeroes the target — so
      //     ezdxf only ever exercises the configuration where the two
      //     readings AGREE. It cannot discriminate, and citing it was an
      //     error (@codex, PR #794).
      //   * the "62 % of entities fall inside the raw box" measurement
      //     counted containment in a 441900 × 230751 window on a drawing
      //     whose content is unevenly spread. That is a proxy for nothing.
      //
      // MEASURED, properly, on the same AC1032 file: it carries 19
      // paper-space VIEWPORT entities, which use the SAME group codes
      // (12/22 centre, 17/27/37 target) and must by construction frame
      // real model content. Of the five whose target is non-zero — the
      // only ones that discriminate — the summed centre frames
      // 50 / 23 / 210 / 41 / 25 non-text model entities while the raw
      // centre frames 0 / 0 / 8 / 0 / 0. Five out of five for the sum.
      //
      // Non-zero target is a normal pan in plan views, NOT a reason to
      // reject the saved view. Only viewDirection ≠ +Z and twist ≠ 0
      // break DCS-WCS axis alignment and are still rejected.
      //
      // "viewDirection ≈ +Z" means PARALLEL to +Z — any positive Z
      // magnitude with negligible X/Y components qualifies. AutoCAD
      // emits (0, 0, 1) most of the time but (0, 0, N) for some saved
      // configurations; both are plan views.
      //
      // Missing components default per-axis to their identity value
      // (viewDir → (0,0,1); target → (0,0,0); twist → 0) so partial
      // tuples are not silently coerced to identity.
      const VIEW_DIR_EPS = 1e-6;
      const TWIST_EPS = 1e-6;
      // DXF viewDirection is a target-to-camera vector. A plan view is
      // any vector PARALLEL to +Z — not just unit (0,0,1). Real files
      // emit (0,0,10) or similar non-unit magnitudes. Reject only when
      // the vector is degenerate, points away from +Z, or has any
      // significant XY component relative to its Z magnitude.
      const isPlanView =
        viewDirZ > 0 &&
        Math.abs(viewDirX) <= VIEW_DIR_EPS * viewDirZ &&
        Math.abs(viewDirY) <= VIEW_DIR_EPS * viewDirZ;
      const twistIsZero = Math.abs(twist) < TWIST_EPS;
      if (!isPlanView || !twistIsZero) {
        return undefined;
      }
      const view: SavedView = {
        center: { x: targetX + centerX, y: targetY + centerY },
        height,
        aspectRatio,
      };
      if (anyTargetGroupSeen) {
        view.target = { x: targetX, y: targetY, z: targetZ };
      }
      if (anyViewDirGroupSeen) {
        view.viewDirection = { x: viewDirX, y: viewDirY, z: viewDirZ };
      }
      return view;
    }
    return undefined;
  } catch {
    return undefined;
  }
}

export function collectUnsupportedEntityGroups(
  document: DxfDocument,
): RawEntityGroup[] {
  const section = getEntitiesSection(document);
  if (!section) return [];
  const out: RawEntityGroup[] = [];
  // Ordered walk so a heavy-POLYLINE terminating SEQEND (SL-22 F1) is captured
  // directly after its VERTEX body; `prev` lets rawPassthroughGroupForEntity tell
  // a POLYLINE terminator apart from an INSERT's (never standalone) SEQEND.
  let prev: DxfEntity | undefined;
  for (const entity of section.entities) {
    const raw = rawPassthroughGroupForEntity(prev, entity);
    if (raw) out.push(raw);
    prev = entity;
  }
  return out;
}

export function importDxfStringToDrawingDocument(
  source: string,
  options: ImportDxfDocumentToDrawingDocumentOptions = {},
): DrawingDocument {
  return importDxfDocumentToDrawingDocument(
    parseDxfDocumentStreaming(source),
    options,
  );
}

/**
 * Progress-aware variant of {@link importDxfStringToDrawingDocument}.
 *
 * Emits `onProgress` at four phase boundaries (tokenize → sections →
 * entities → bridge), yielding the event loop between phases so the
 * React UI can paint the progress dialog update. The phases produced
 * here are a subset of `IMPORT_PHASE_ORDER`:
 *
 *   tokenize, sections, entities, bridge
 *
 * The caller (editorStore.importDxfText) owns the surrounding fetch /
 * decode / merge / sync phases and emits them around this call to give
 * the UI the full 0→100% bar.
 *
 * The entities-phase progress is approximate — we report total entity
 * counts from the parsed DxfDocument after tokenization, but the
 * conversion to DrawingEntity happens inside the synchronous
 * `buildImportedSpaces` call that we cannot chunk without a much
 * deeper refactor of `dxfBridge.spaces.ts`. For MVP the entities
 * phase reports `itemsProcessed = itemsTotal` (100%) when the bridge
 * call returns.
 */
export async function importDxfStringToDrawingDocumentWithProgress(
  source: string,
  options: ImportDxfDocumentToDrawingDocumentOptions = {},
): Promise<DrawingDocument> {
  const onProgress = options.onProgress;

  // ── Phase: tokenize ─────────────────────────────────────────────────
  onProgress?.("tokenize", 0, 0, 1);
  await yieldForPaint();
  const dxfDoc = parseDxfDocumentStreaming(source);
  onProgress?.("tokenize", 100, 1, 1);
  await yieldForPaint();

  // ── Phase: sections ────────────────────────────────────────────────
  // parseDxfDocument already grouped sections; we treat this phase as
  // a near-instant checkpoint used by the UI to show "Parsing sections"
  // between the heavy tokenize and entities phases.
  onProgress?.("sections", 0, 0, 1);
  await yieldForPaint();
  onProgress?.("sections", 100, 1, 1);

  // ── Phase: entities ─────────────────────────────────────────────────
  // Count total entities across all sections (ENTITIES + BLOCKS child
  // entities) so the UI can format "X / Y" alongside the bar. Bridge
  // conversion happens synchronously below — we report completion once
  // it returns.
  const entityCount = countEntitiesInDxfDocument(dxfDoc);
  onProgress?.("entities", 0, 0, Math.max(1, entityCount));
  await yieldForPaint();
  onProgress?.(
    "entities",
    100,
    Math.max(1, entityCount),
    Math.max(1, entityCount),
  );
  await yieldForPaint();

  // ── Phase: bridge ──────────────────────────────────────────────────
  onProgress?.("bridge", 0, 0, 1);
  await yieldForPaint();
  const drawing = importDxfDocumentToDrawingDocument(dxfDoc, options);
  onProgress?.("bridge", 100, 1, 1);

  return drawing;
}

/**
 * Extract Dynamic Input system variable values ($DYNMODE / $DYNDIGRIP / …)
 * from the HEADER raw passthrough section of an already-imported
 * `DrawingDocument`.
 *
 * Called by App.tsx immediately after loading a DXF file to hydrate the
 * `sysvarStore` with the document's persisted DI settings (AutoCAD parity).
 * Returns an empty record when no HEADER section was captured or the document
 * predates the DYN* variables (legacy DXF files — falls back to localStorage
 * / DYN_DEFAULTS in the store).
 */
export function readDynVarsFromDrawingDocument(
  document: DrawingDocument,
): Partial<Record<DynVarHeaderKey, number>> {
  const headerSection = (document.rawPassthroughSections ?? []).find(
    (s) => s.name === "HEADER",
  );
  if (!headerSection) return {};
  return readDynVarsFromHeader(
    headerSection.groups.map((g) => ({ code: g.code, value: g.value })),
  );
}

/**
 * Sum up entities across every DXF section so the UI can format a
 * denominator for the progress bar. Covers ENTITIES and BLOCKS
 * (child-entity count) sections only — OBJECTS are counted as
 * meta-data, not as drawing entities.
 */
export function countEntitiesInDxfDocument(doc: DxfDocument): number {
  let total = 0;
  for (const section of doc.sections) {
    if (section.kind === "entities-section") {
      total += section.entities.length;
    } else if (section.kind === "blocks-section") {
      for (const block of section.blocks) {
        total += block.entities.length;
      }
    }
  }
  return total;
}
